import React, { useState, useEffect, useMemo } from 'react'
import { AlertControlsSection } from './AlertControlsSection'
import { AlertSettingsSection } from './AlertSettingsSection'
import { WaterThresholdsSection } from './WaterThresholdsSection'
import { AccountAlertSettingsSection } from './BatteryThresholdsSection'
import { LoadingSpinner } from '../../shared/components/ui/LoadingSpinner'
import Button from '../../shared/components/ui/Button'
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { useAlertControls, useUpdateAlertControls, AlertControl } from '../../hooks/useAlertControls'
import { useAlertSettings, useCreateAlertSettings, useUpdateAlertSettings } from '../../hooks/useAlertSettings'
import { useHysteresisSettings, useUpdateHysteresisSettings } from '../../hooks/useHysteresisSettings'
import { AlertTypeEnums, ContactTypeEnums, ALERT_SETTINGS_GLOBAL_DEVICE_ID, AlertSettingsItem } from '../../common/constants'

interface AlertControlsState {
  [alertType: string]: boolean
}

interface AlertSettingsState {
  [alertType: string]: {
    [key: string]: AlertSettingsItem | undefined // key format: `${contactType}_${itemOrder}`
  }
}

export function NotificationSettings() {
  const [advancedMode, setAdvancedMode] = useState(false)

  // Fetch data from API
  const { data: alertControlsData, isLoading: alertControlsLoading, error: alertControlsError } = useAlertControls()
  const { data: alertSettingsData, isLoading: alertSettingsLoading, error: alertSettingsError } = useAlertSettings(ALERT_SETTINGS_GLOBAL_DEVICE_ID, true)
  const { data: hysteresisData, isLoading: hysteresisLoading, error: hysteresisError } = useHysteresisSettings()

  // Mutations
  const updateAlertControlsMutation = useUpdateAlertControls()
  const createAlertSettingsMutation = useCreateAlertSettings()
  const updateAlertSettingsMutation = useUpdateAlertSettings()
  const updateHysteresisMutation = useUpdateHysteresisSettings()

  // Original state (from API)
  const [originalAlertControls, setOriginalAlertControls] = useState<AlertControlsState>({})
  const [originalAlertSettings, setOriginalAlertSettings] = useState<AlertSettingsState>({})
  const [originalHysteresis, setOriginalHysteresis] = useState<number>(0)

  // Current state (user edits)
  const [currentAlertControls, setCurrentAlertControls] = useState<AlertControlsState>({})
  const [currentAlertSettings, setCurrentAlertSettings] = useState<AlertSettingsState>({})
  const [currentHysteresis, setCurrentHysteresis] = useState<number>(0)

  const [isSaving, setIsSaving] = useState(false)

  // Load advanced mode preference from localStorage
  useEffect(() => {
    const savedMode = localStorage.getItem('alertsAdvancedMode')
    if (savedMode !== null) {
      setAdvancedMode(savedMode === 'true')
    }
  }, [])

  // Initialize state from API data
  useEffect(() => {
    if (alertControlsData) {
      const controlsState: AlertControlsState = {}
      alertControlsData.forEach((control) => {
        controlsState[control.alertType] = control.enabled
      })
      setOriginalAlertControls(controlsState)
      setCurrentAlertControls(controlsState)
    }
  }, [alertControlsData])

  useEffect(() => {
    if (alertSettingsData?.data) {
      const settingsState: AlertSettingsState = {}

      // Initialize structure for all possible alert types
      Object.values(AlertTypeEnums).forEach((alertType) => {
        settingsState[alertType] = {}
      })

      // Populate with existing settings
      alertSettingsData.data.forEach((setting) => {
        const key = `${setting.contactType}_${setting.itemOrder}`
        if (!settingsState[setting.alertType]) {
          settingsState[setting.alertType] = {}
        }
        settingsState[setting.alertType][key] = setting
      })

      setOriginalAlertSettings(settingsState)
      setCurrentAlertSettings(JSON.parse(JSON.stringify(settingsState))) // Deep clone
    }
  }, [alertSettingsData])

  useEffect(() => {
    if (hysteresisData?.hysteresis !== undefined) {
      setOriginalHysteresis(hysteresisData.hysteresis)
      setCurrentHysteresis(hysteresisData.hysteresis)
    }
  }, [hysteresisData])

  // Detect changes
  const hasChanges = useMemo(() => {
    // Check alert controls changes
    const controlsChanged = Object.keys(currentAlertControls).some(
      (alertType) => currentAlertControls[alertType] !== originalAlertControls[alertType]
    )

    // Check alert settings changes
    const settingsChanged = Object.keys(currentAlertSettings).some((alertType) => {
      const currentSettings = currentAlertSettings[alertType]
      const originalSettings = originalAlertSettings[alertType]

      return Object.keys(currentSettings || {}).some((key) => {
        const current = currentSettings?.[key]
        const original = originalSettings?.[key]

        if (!current && !original) return false
        if (!current || !original) return true

        return (
          current.enabled !== original.enabled ||
          current.contact !== original.contact ||
          current.message !== original.message ||
          current.totalAttempts !== original.totalAttempts ||
          current.retryDelay !== original.retryDelay
        )
      })
    })

    // Check hysteresis changes
    const hysteresisChanged = currentHysteresis !== originalHysteresis

    return controlsChanged || settingsChanged || hysteresisChanged
  }, [currentAlertControls, originalAlertControls, currentAlertSettings, originalAlertSettings, currentHysteresis, originalHysteresis])

  // Save advanced mode preference to localStorage
  const handleAdvancedModeToggle = (enabled: boolean) => {
    setAdvancedMode(enabled)
    localStorage.setItem('alertsAdvancedMode', enabled.toString())
  }

  // Reset handler - revert to original state
  const handleReset = () => {
    setCurrentAlertControls({ ...originalAlertControls })
    setCurrentAlertSettings(JSON.parse(JSON.stringify(originalAlertSettings))) // Deep clone
    setCurrentHysteresis(originalHysteresis)
  }

  // Save handler - batch all API calls
  const handleSave = async () => {
    if (!hasChanges) return

    setIsSaving(true)

    try {
      // Save alert controls changes
      for (const alertType of Object.keys(currentAlertControls)) {
        if (currentAlertControls[alertType] !== originalAlertControls[alertType]) {
          await updateAlertControlsMutation.mutateAsync({
            alertType,
            enabled: currentAlertControls[alertType],
          })
        }
      }

      // Save alert settings changes
      for (const alertType of Object.keys(currentAlertSettings)) {
        const currentSettings = currentAlertSettings[alertType]
        const originalSettings = originalAlertSettings[alertType]

        for (const key of Object.keys(currentSettings || {})) {
          const current = currentSettings?.[key]
          const original = originalSettings?.[key]

          if (current && original) {
            // Check if changed
            const changed =
              current.enabled !== original.enabled ||
              current.contact !== original.contact ||
              current.message !== original.message ||
              current.totalAttempts !== original.totalAttempts ||
              current.retryDelay !== original.retryDelay

            if (changed) {
              if (original.id) {
                // Update existing
                await updateAlertSettingsMutation.mutateAsync(current)
              } else {
                // Create new
                const created = await createAlertSettingsMutation.mutateAsync(current)
                // Update current state with created ID
                if (created && typeof created === 'object' && 'id' in created) {
                  current.id = (created as AlertSettingsItem).id
                }
              }
            }
          } else if (current && !original) {
            // New setting - create
            const created = await createAlertSettingsMutation.mutateAsync(current)
            if (created && typeof created === 'object' && 'id' in created) {
              current.id = (created as AlertSettingsItem).id
            }
          }
        }
      }

      // Save hysteresis changes
      if (currentHysteresis !== originalHysteresis) {
        await updateHysteresisMutation.mutateAsync(currentHysteresis)
      }

      // Update original state to match current (successful save)
      setOriginalAlertControls({ ...currentAlertControls })
      setOriginalAlertSettings(JSON.parse(JSON.stringify(currentAlertSettings)))
      setOriginalHysteresis(currentHysteresis)
    } catch (error) {
      console.error('Failed to save notification settings:', error)
      // Optionally show error toast/notification
    } finally {
      setIsSaving(false)
    }
  }

  // Handlers for child components to update current state
  const handleAlertControlChange = (alertType: string, enabled: boolean) => {
    setCurrentAlertControls((prev) => ({
      ...prev,
      [alertType]: enabled,
    }))
  }

  const handleAlertSettingChange = (alertType: AlertTypeEnums, contactType: ContactTypeEnums, itemOrder: number, setting: Partial<AlertSettingsItem>) => {
    setCurrentAlertSettings((prev) => {
      const key = `${contactType}_${itemOrder}`
      const alertTypeSettings = prev[alertType] || {}
      const existingSetting = alertTypeSettings[key]

      return {
        ...prev,
        [alertType]: {
          ...alertTypeSettings,
          [key]: {
            ...existingSetting,
            ...setting,
            alertType,
            contactType,
            itemOrder,
            deviceId: ALERT_SETTINGS_GLOBAL_DEVICE_ID,
          } as AlertSettingsItem,
        },
      }
    })
  }

  const handleHysteresisChange = (value: number) => {
    setCurrentHysteresis(value)
  }

  // Loading state
  const isLoading = alertControlsLoading || alertSettingsLoading || hysteresisLoading

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  // Error state
  const error = alertControlsError || alertSettingsError || hysteresisError
  if (error) {
    return (
      <div className="space-y-6 p-6">
        <div className="text-center py-8">
          <p className="text-sm text-red-600">Failed to load alert settings</p>
          <p className="text-xs text-gray-500 mt-1">{error.message}</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6 p-6">
      {/* Header with Save/Reset Buttons */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Alert Settings</h3>
          <p className="mt-1 text-sm text-gray-600">
            Configure how and when you want to receive notifications
          </p>
        </div>

        <div className="flex items-center space-x-3">
          {/* Advanced Mode Toggle */}
          <div className="flex items-center space-x-2 mr-4">
            <label htmlFor="advanced-mode" className="text-sm font-medium text-gray-700">
              Advanced Mode
            </label>
            <input
              type="checkbox"
              id="advanced-mode"
              checked={advancedMode}
              onChange={(e) => handleAdvancedModeToggle(e.target.checked)}
              className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
          </div>

          {/* Save/Reset Buttons */}
          {isSaving && (
            <div className="flex items-center text-sm text-gray-600">
              <LoadingSpinner size="sm" className="mr-2" />
              Saving...
            </div>
          )}
          <Button variant="outline" size="sm" onClick={handleReset} disabled={!hasChanges || isSaving}>
            Reset
          </Button>
          <Button variant="primary" size="sm" onClick={handleSave} disabled={!hasChanges} loading={isSaving}>
            Save Changes
          </Button>
        </div>
      </div>

      {/* Alert Controls Section */}
      <AlertControlsSection
        advancedMode={advancedMode}
        alertControls={currentAlertControls}
        onAlertControlChange={handleAlertControlChange}
        disabled={isSaving}
      />

      {/* Alert Settings Section */}
      <AlertSettingsSection
        advancedMode={advancedMode}
        alertSettings={currentAlertSettings}
        onAlertSettingChange={handleAlertSettingChange}
        disabled={isSaving}
      />

      {/* Water Thresholds Section */}
      <WaterThresholdsSection
        hysteresis={currentHysteresis}
        onHysteresisChange={handleHysteresisChange}
        disabled={isSaving}
      />

      {/* Account Alert Settings Section */}
      <AccountAlertSettingsSection />
    </div>
  )
}
